import { useEffect, useRef, useState } from "react";
import type { UploadedAssetManifest, UploadedAssetRecord } from "../types";
import { Icon } from "./Icon";

type AssetBucket = keyof UploadedAssetManifest;

interface ImportAssetsModalProps {
  initialBucket?: AssetBucket;
  manifest?: UploadedAssetManifest;
  onClose: () => void;
  onUploadAssets: (bucket: AssetBucket, files: File[]) => void;
}

const buckets: { id: AssetBucket; label: string; detail: string; accept: string }[] = [
  {
    id: "characterCards",
    label: "Character cards",
    detail: "Wardrobe, face, body, and continuity references.",
    accept: "image/*,.pdf"
  },
  {
    id: "sceneReferences",
    label: "Scene references",
    detail: "Locations, mood boards, production design, storyboard sheets.",
    accept: "image/*,.pdf"
  },
  {
    id: "voiceAudio",
    label: "Voice audio",
    detail: "Actor reads, narration, temp VO, or lip-sync source lines.",
    accept: "audio/*,.m4a,.mp3,.wav,.aiff"
  },
  {
    id: "music",
    label: "Music",
    detail: "Temp score, cue references, or owned background music.",
    accept: "audio/*,.m4a,.mp3,.wav,.aiff"
  },
  {
    id: "sfx",
    label: "SFX",
    detail: "Ambience, impacts, vehicles, room tone, and design notes.",
    accept: "audio/*,.pdf,.txt"
  },
  {
    id: "videoReferences",
    label: "Video references",
    detail: "Motion & style clips — camera moves, choreography, tone. Seedance uses up to 3 per shot.",
    accept: "video/*,.mp4,.mov,.webm"
  },
  {
    id: "styleRefs",
    label: "Style / AI refs",
    detail: "Look, grade, and AI-generation reference imagery.",
    accept: "image/*,.png,.jpg,.jpeg,.webp"
  },
  {
    id: "branding",
    label: "Branding & props",
    detail: "In-world logos, products, signage, key props.",
    accept: "image/*,.pdf"
  },
  {
    id: "documents",
    label: "Documents",
    detail: "Bibles, treatments, edit notes, lookbooks.",
    accept: ".pdf,.docx,.txt,.md"
  }
];

function statusChip(record: UploadedAssetRecord): { label: string; tone: string } {
  if (record.cloudStatus === "pending") return { label: "Uploading…", tone: "pending" };
  if (record.cloudStatus === "failed") return { label: "Failed", tone: "failed" };
  if (record.cloudStatus === "local_only") return { label: "Local only", tone: "local" };
  return { label: "Saved", tone: "saved" };
}

/**
 * Import modal with live feedback: every file that lands in a bucket is listed immediately with its
 * upload status, so a successful pick never looks like "nothing happened".
 */
export function ImportAssetsModal({ initialBucket = "characterCards", manifest, onClose, onUploadAssets }: ImportAssetsModalProps) {
  const [activeBucket, setActiveBucket] = useState<AssetBucket>(initialBucket);
  const [lastUploadCount, setLastUploadCount] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const active = buckets.find((bucket) => bucket.id === activeBucket) || buckets[0];
  const bucketAssets = manifest?.[activeBucket] || [];
  const recentAssets = [...bucketAssets].reverse().slice(0, 6);

  useEffect(() => {
    setActiveBucket(initialBucket);
  }, [initialBucket]);

  useEffect(() => {
    setLastUploadCount(0);
  }, [activeBucket]);

  return (
    <div className="modal-backdrop" role="presentation">
      <section className="import-modal" aria-label="Import production assets" role="dialog" aria-modal="true">
        <header>
          <div>
            <span>Import</span>
            <h2>Add assets before AI planning</h2>
            <p>Uploads and locked assets become source-of-truth inputs. Empty storyboard slots can still be generated later.</p>
          </div>
          <button className="icon-button" onClick={onClose} type="button" aria-label="Close import modal">
            x
          </button>
        </header>

        <div className="import-layout">
          <nav className="import-bucket-list" aria-label="Asset categories">
            {buckets.map((bucket) => {
              const count = manifest?.[bucket.id]?.length || 0;
              return (
                <button className={bucket.id === activeBucket ? "active" : ""} key={bucket.id} onClick={() => setActiveBucket(bucket.id)} type="button">
                  <strong>
                    {bucket.label}
                    {count ? <b className="import-bucket-count">{count}</b> : null}
                  </strong>
                  <small>{bucket.detail}</small>
                </button>
              );
            })}
          </nav>

          <div className="import-drop-panel">
            <input
              ref={inputRef}
              className="visually-hidden"
              type="file"
              multiple
              accept={active.accept}
              onChange={(event) => {
                const files = Array.from(event.target.files || []);
                if (files.length) {
                  onUploadAssets(activeBucket, files);
                  setLastUploadCount(files.length);
                }
                event.target.value = "";
              }}
            />
            <button className="dropzone import-dropzone" onClick={() => inputRef.current?.click()} type="button">
              <Icon name="upload" />
              <strong>Browse {active.label.toLowerCase()}</strong>
              <small>{active.detail}</small>
            </button>
            {lastUploadCount ? (
              <p className="import-upload-confirm" role="status">
                ✓ {lastUploadCount} file{lastUploadCount === 1 ? "" : "s"} added to {active.label.toLowerCase()} — listed below.
              </p>
            ) : null}
            {recentAssets.length ? (
              <div className="import-asset-list" aria-label={`${active.label} already in this project`} aria-live="polite">
                <span>
                  In this project · {bucketAssets.length} {active.label.toLowerCase()}
                </span>
                {recentAssets.map((record, index) => {
                  const chip = statusChip(record);
                  return (
                    <article className="import-asset-row" key={record.assetId || `${record.fileName}-${index}`}>
                      <div>
                        <strong>{record.label || record.fileName}</strong>
                        <small>{record.fileName}</small>
                      </div>
                      <b className={`import-asset-status ${chip.tone}`}>{chip.label}</b>
                    </article>
                  );
                })}
                {bucketAssets.length > recentAssets.length ? (
                  <small className="import-asset-more">+ {bucketAssets.length - recentAssets.length} more in the Asset Library</small>
                ) : null}
              </div>
            ) : (
              <p className="import-asset-empty">Nothing in {active.label.toLowerCase()} yet — files you pick will appear here instantly.</p>
            )}
            <div className="import-rules">
              <span>Rules</span>
              <p>User uploads win over generated output. The production agent can suggest missing files, but it cannot overwrite locked material.</p>
              <p>Provider export stays dry-run until the director approves scene scope, categories, rights, and cost.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
